// src/components/ProductList.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

// --- localStorage helper (hook)
function useLocalStorage(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const s = localStorage.getItem(key);
      return s ? JSON.parse(s) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [key, state]);
  return [state, setState];
}

// 1:1 sa MySQL kolonama
const ALL_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "ExternalID", label: "External ID" },
  { key: "DistributorCode", label: "Distributor Code" },
  { key: "DistributorName", label: "Distributor Name" },
  { key: "Manufacturer", label: "Manufacturer" },
  { key: "ManufacturerCode", label: "Manufacturer Code" },
  { key: "ManufacturerName", label: "Manufacturer Name" },
  { key: "Importer", label: "Importer" },
  { key: "CountryOfOrigin", label: "Country of Origin" },
  { key: "Thickness", label: "Thickness" },
  { key: "Width", label: "Width" },
  { key: "Height", label: "Height" },
  { key: "UnitOfMeasure", label: "UoM" },
  { key: "NetWeightKgPerUnit", label: "Net Wt. (kg/unit)" },
  { key: "Packaging", label: "Packaging" },
  { key: "QuantityPerPackage", label: "Qty/Package" },
  { key: "Density", label: "Density" },
  { key: "Category", label: "Category" },
  { key: "SubCategory", label: "Sub Category" },
  { key: "Surface", label: "Surface" },
  { key: "DecorGroup", label: "Decor Group" },
  { key: "DecorCode", label: "Decor Code" },
  { key: "Structure", label: "Structure" },
  { key: "PhotoNote", label: "Photo Note" },
  { key: "Application", label: "Application" },
  { key: "ApplicationAndMaintenanceInstructions", label: "App & Maint. Instr." },
  { key: "DiscountedPrice", label: "Discounted Price" },
  { key: "OriginalPrice", label: "Original Price" },
  { key: "URL", label: "URL" },
  { key: "Availability", label: "Availability" },
  { key: "CurrentPrice", label: "Current Price" },
  { key: "NumberInStock", label: "In Stock" },
  { key: "PriceCurrency", label: "Currency" },
  { key: "Function", label: "Function" },
  { key: "ThicknessValue", label: "Thickness (value)" },
];

const DEFAULT_VISIBLE = [
  "DistributorName","DistributorCode","DecorCode","Structure","Importer","Category","SubCategory",
  "Thickness","Width","Height","UnitOfMeasure",
  "CurrentPrice","PriceCurrency","NumberInStock",
];

const NUMERIC = new Set([
  "Thickness","Width","Height","NetWeightKgPerUnit","QuantityPerPackage","Density",
  "DiscountedPrice","OriginalPrice","CurrentPrice","NumberInStock","ThicknessValue"
]);

export default function ProductList({
  products,
  loading,
  globalFilter = "",
  page,
  pageSize,
  total,                     // null => bez pager-a
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onCreate,
  onDuplicate,
}) {
  // ------- per-kolona filteri -------
  const [columnFilters, setColumnFilters] = useState([]);

  const toNum = (v) => {
    if (v == null || v === "") return null;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const textContains = (row, columnId, filterValue) => {
    const val = String(row.getValue(columnId) ?? "").toLowerCase();
    const q = String(filterValue ?? "").toLowerCase();
    return val.includes(q);
  };

  const inNumberRange = (row, columnId, range) => {
    const v = toNum(row.getValue(columnId));
    if (v == null) return false;
    const min = toNum(range?.min);
    const max = toNum(range?.max);
    if (min != null && v < min) return false;
    if (max != null && v > max) return false;
    return true;
  };

  // ------- kolone + resize -------
  const baseCols = useMemo(() => ALL_COLUMNS.map(c => ({
    accessorKey: c.key,
    header: c.label,
    size: 160,
    minSize: 90,
    maxSize: 500,
    sortingFn: (rowA, rowB, colId) => {
      if (!NUMERIC.has(colId)) {
        const a = String(rowA.getValue(colId) ?? "").toLowerCase();
        const b = String(rowB.getValue(colId) ?? "").toLowerCase();
        return a.localeCompare(b);
      }
      const a = parseFloat(String(rowA.getValue(colId) ?? "").replace(",", ".")) || 0;
      const b = parseFloat(String(rowB.getValue(colId) ?? "").replace(",", ".")) || 0;
      return a === b ? 0 : a > b ? 1 : -1;
    },
    cell: info => String(info.getValue() ?? ""),
    filterFn: NUMERIC.has(c.key) ? "inNumberRange" : "textContains",
  })), []);

  const columns = useMemo(() => {
  const actionCols = [];

  if (onEdit || onDelete || onDuplicate) {
    actionCols.push({
      id: "actions",
      header: "Actions",
      size: 120,
      minSize: 120,
      maxSize: 200,
      enableResizing: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          {onEdit && (
            <button
              type="button"
              className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
              onClick={() => onEdit(row.original)}
              title="Edit"
            >
              ✏️
            </button>
          )}

          {onDuplicate && (
            <button
              type="button"
              className="bg-emerald-500 text-white px-2 py-1 rounded text-xs hover:bg-emerald-600"
              onClick={() => onDuplicate(row.original)}
              title="Duplicate"
            >
              ⧉
            </button>
          )}

          {onDelete && (
            <button
              type="button"
              className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
              onClick={() => onDelete(row.original.id)}
              title="Delete"
            >
              🗑️
            </button>
          )}
        </div>
      ),
    });
  }

  return [...baseCols, ...actionCols];
}, [baseCols, onEdit, onDelete, onDuplicate]);

  // ------- vidljivost/redosled + sizing (persist) -------
  const [columnVisibility, setColumnVisibility] = useLocalStorage(
    "products.v2.colVisibility",
    Object.fromEntries(ALL_COLUMNS.map(c => [c.key, DEFAULT_VISIBLE.includes(c.key)]))
  );
  const [columnOrder, setColumnOrder] = useLocalStorage(
    "products.v2.colOrder",
    [...DEFAULT_VISIBLE, ...ALL_COLUMNS.map(c => c.key).filter(k => !DEFAULT_VISIBLE.includes(k)), "actions"]
  );
  const [columnSizing, setColumnSizing] = useLocalStorage("products.v2.colSizing", {});
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [sorting, setSorting] = useState([{ id: "ManufacturerName", desc: false }]);

  const table = useReactTable({
    data: products || [],
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,              // <—
      columnVisibility, columnOrder,
      columnSizing, columnSizingInfo,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters, // <—
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: row => row.id ?? `${row.DistributorCode}-${row.ManufacturerName}`,
    globalFilterFn: (row, _col, filter) => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return ALL_COLUMNS.some(c => String(row.original?.[c.key] ?? "").toLowerCase().includes(q));
    },
    filterFns: { textContains, inNumberRange }, // <—
    columnResizeMode: "onChange",
  });

  // ------- columns panel -------
  const [openCols, setOpenCols] = useState(false);
  const ordered = columnOrder
    .map(k => (k === "actions" ? { key: "actions", label: "Actions" } : ALL_COLUMNS.find(c => c.key === k)))
    .filter(Boolean);

  const moveCol = (key, dir) => {
    setColumnOrder(prev => {
      const next = [...prev];
      const i = next.indexOf(key);
      if (i === -1) return prev;
      const j = dir === "left" ? i - 1 : i + 1;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  if (loading) return <div className="py-6 text-center">Loading…</div>;

  const rows = table.getRowModel().rows;
  const pageStart = total ? (page - 1) * pageSize + 1 : null;
  const pageEnd   = total ? Math.min(page * pageSize, total) : null;

  return (
    <div className="space-y-3">
        
      {/* Columns panel */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setOpenCols(o => !o)}
          className="px-3 py-2 rounded border hover:bg-gray-50 dark:hover:bg-neutral-800"
        >Columns</button>
        {openCols && (
          <div className="z-20 border rounded shadow bg-white dark:bg-neutral-900 p-3 max-h-96 overflow-auto">
            {ordered.map(c => (
              <div key={c.key} className="flex items-center justify-between py-1 gap-3">
                <label className="flex items-center gap-2">
                  {c.key !== "actions" && (
                    <input
                      type="checkbox"
                      checked={!!columnVisibility[c.key]}
                      onChange={() =>
                        setColumnVisibility(v => ({ ...v, [c.key]: !v[c.key] }))
                      }
                    />
                  )}
                  {c.label}
                </label>
                <div className="flex gap-1">
                  <button className="px-2 py-0.5 border rounded text-xs" onClick={() => moveCol(c.key, "left")}>◀</button>
                  <button className="px-2 py-0.5 border rounded text-xs" onClick={() => moveCol(c.key, "right")}>▶</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            className="ml-auto bg-primary-light text-white px-4 py-2 rounded hover:bg-primary-dark"
          >
            + Add product
          </button>
        )}
      </div>

      {/* Tabela + filter red */}
      <div className="overflow-auto border rounded max-h-[70vh]">
        <table className="min-w-full border-collapse table-fixed">
          <thead className="bg-gray-100 dark:bg-neutral-800 sticky top-0 z-10">
            {/* Naslovi */}
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => {
                  const canResize = header.column.getCanResize();
                  const isResizing = header.column.getIsResizing();
                  const size = header.column.getSize();
                  return (
                    <th
                      key={header.id}
                      className="px-3 py-2 border text-left whitespace-nowrap cursor-pointer select-none relative"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ width: size, maxWidth: size, minWidth: size }}
                      title="Click to sort. Drag right edge to resize."
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === "asc" ? " ↑" : header.column.getIsSorted() === "desc" ? " ↓" : ""}
                      {canResize && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute top-0 right-0 h-full w-1 cursor-col-resize select-none touch-none
                                      ${isResizing ? "bg-blue-500" : "bg-transparent"}`}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}

            {/* Filter red */}
            <tr>
              {table.getVisibleLeafColumns().map(col => {
                const size = col.getSize();
                if (col.id === "actions") {
                  return <th key={col.id} style={{width:size,minWidth:size,maxWidth:size}} className="px-2 py-1 border" />;
                }
                if (NUMERIC.has(col.id)) {
                  const v = col.getFilterValue() || {};
                  return (
                    <th key={col.id} style={{width:size,minWidth:size,maxWidth:size}} className="px-2 py-1 border">
                      <div className="flex gap-1">
                        <input
                          type="number" placeholder="min"
                          value={v.min ?? ""}
                          onChange={(e) => col.setFilterValue(prev => ({...(prev||{}), min: e.target.value || undefined}))}
                          className="w-1/2 border rounded px-1 py-0.5"
                        />
                        <input
                          type="number" placeholder="max"
                          value={v.max ?? ""}
                          onChange={(e) => col.setFilterValue(prev => ({...(prev||{}), max: e.target.value || undefined}))}
                          className="w-1/2 border rounded px-1 py-0.5"
                        />
                      </div>
                    </th>
                  );
                }
                return (
                  <th key={col.id} style={{width:size,minWidth:size,maxWidth:size}} className="px-2 py-1 border">
                    <input
                      placeholder="filter…"
                      value={col.getFilterValue() ?? ""}
                      onChange={(e) => col.setFilterValue(e.target.value)}
                      className="w-full border rounded px-1 py-0.5"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                {row.getVisibleCells().map(cell => {
                  const w = cell.column.getSize();
                  return (
                    <td
                      key={cell.id}
                      className="px-3 py-2 border whitespace-nowrap overflow-hidden text-ellipsis"
                      style={{ width: w, maxWidth: w, minWidth: w }}
                      title={String(cell.getValue() ?? "")}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={table.getVisibleFlatColumns().length} className="px-3 py-4 text-center text-gray-500">
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      {total != null && (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            Showing {pageStart}-{pageEnd} of {total}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
              className="border rounded px-2 py-1"
            >
              {[25,50,100,200,1000,10000].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button className="px-2 py-1 border rounded" disabled={page<=1} onClick={() => onPageChange?.(page-1)}>Prev</button>
            <button className="px-2 py-1 border rounded" disabled={page*pageSize >= total} onClick={() => onPageChange?.(page+1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
